/**
 * oRPC client factory for frontend features.
 *
 * Creates type-safe API clients using oRPC with contracts from @animus/api-treaty.
 * Integrates with the existing ApiConfig context for auth strategy.
 */

import { createORPCClient, safe } from "@orpc/client";
import { OpenAPILink } from "@orpc/openapi-client/fetch";
import { Result } from "true-myth/result";

import { type ApiClient, apiContract } from "@animus/api-treaty";

import type { AuthStrategy } from "./api-config";

/** API error with typed error codes */
export interface OrpcApiError {
  type: "api" | "unauthorized" | "network";
  code: string | undefined;
  message: string;
}

/** Check if an oRPC error indicates unauthorized (401) */
function isUnauthorizedError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    error.code === "UNAUTHORIZED"
  );
}

/** Extract error code from an oRPC error */
function getErrorCode(error: unknown): string | undefined {
  if (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof error.code === "string"
  ) {
    return error.code;
  }
  return;
}

/** Extract message from an oRPC error */
function getErrorMessage(error: unknown, fallback: string): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }
  return fallback;
}

/**
 * Create an oRPC client with the given base URL and auth strategy.
 *
 * Uses OpenAPILink which sends requests to the explicit paths defined
 * in the contracts (e.g., /rpc/nutrition/foods instead of /rpc/nutritionFoods/list).
 */
export function createApiClient(
  baseUrl: string,
  auth: AuthStrategy
): ApiClient {
  const link = new OpenAPILink(apiContract, {
    url: `${baseUrl}/rpc`,
    headers: () => {
      const headers: Record<string, string> = {};
      if (auth.type === "bearer") {
        const token = auth.getToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      }
      return headers;
    },
    fetch: (input, init) => {
      const fetchInit: RequestInit = {
        ...init,
        credentials: auth.type === "cookie" ? "include" : "omit",
      };
      return fetch(input, fetchInit);
    },
  });

  return createORPCClient(link);
}

/**
 * Call an oRPC endpoint and return a True Myth Result.
 *
 * Converts oRPC's throwing pattern to Result for consistent error handling.
 */
export async function callApi<T>(
  call: Promise<T>,
  fallbackMessage = "API call failed"
): Promise<Result<T, OrpcApiError>> {
  const [error, data] = await safe(call);

  if (error) {
    const isUnauthorized = isUnauthorizedError(error);
    return Result.err({
      type: isUnauthorized ? "unauthorized" : "api",
      code: getErrorCode(error),
      message: getErrorMessage(error, fallbackMessage),
    });
  }

  return Result.ok(data);
}
